/**
 * An error, opened (user, 20/10/2026: "why did it fail?"): three plain
 * blocks — What happened (the headline, what the automation did next), Why
 * (the exact reason), What to do (the kind's advice, with the buttons that
 * act on it: swap the container's prompt group (21/10/2026), open the
 * prompt rule that failed right here, fill the texts again, open the
 * container) — then the details in one muted line: the drop, the repeats,
 * Slack. A resolved error says what fixed it instead of what to do
 * (user, 21/10/2026: "I must be able to see that it has been resolved").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useState } from 'react';
import { ArrowLeftRight, CheckCircle2, PenLine, Sparkles } from 'lucide-react';
import type { DropGenErrorRow } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import { useDropGeneratorMutations } from '../../../useDropGeneratorMutations';
import { instantText } from '../../../utils/clock/dk-label';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { SectionAction } from '../../timeline/section/SectionAction';
import { kindWords } from '../errors-words';
import { OpenPromptGroup } from './OpenPromptGroup';
import { SwapPanel } from './swap/SwapPanel';

const Block: FC<{ title: string; children: ReactNode; tint?: string }> = ({ title, children, tint }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className={`text-[10px] font-semibold uppercase tracking-wider ${tint ?? 'text-muted-foreground'}`}>{title}</span>
    <div className="text-[12px] leading-relaxed">{children}</div>
  </div>
);

const FillAgain: FC<{ countryKey: string }> = ({ countryKey }) => {
  const { startCreativeFill } = useDropGeneratorMutations();
  const { success, error } = useToast();
  const run = async () => {
    try {
      await startCreativeFill.mutateAsync(countryKey);
      success('Filling the texts again — the board shows the progress');
    } catch (err) {
      error('Could not start the fill', getApiErrorMessage(err));
    }
  };
  return (
    <SectionAction icon={Sparkles} onClick={() => void run()} disabled={startCreativeFill.isPending}>
      Fill texts again
    </SectionAction>
  );
};

/** A texts error (not sender names) can swap its container's prompt group. */
const swappable = (row: DropGenErrorRow) => row.kind === 'creative_failed' && Boolean(row.groupId) && row.detail?.what !== 'senders' && !row.headline.includes('without sender names');

const Resolved: FC<{ row: DropGenErrorRow }> = ({ row }) => {
  const clock = useCountryClock();
  return (
    <span className="flex items-start gap-1.5 text-success" data-testid="error-resolved">
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>Nothing to do — resolved {instantText(row.resolvedAt!, clock)}: {row.resolution}.</span>
    </span>
  );
};

const Actions: FC<{ row: DropGenErrorRow; countryKey: string; onSwap: () => void; onEditContainer?: (containerId: string) => void }> = ({ row, countryKey, onSwap, onEditContainer }) => {
  const canSwap = useAuth().hasPermission('drop_generator.manage') && swappable(row);
  return (
    <span className="mt-2 flex flex-wrap gap-1.5">
      {canSwap ? <SectionAction icon={ArrowLeftRight} onClick={onSwap}>Swap prompt group</SectionAction> : null}
      {row.kind === 'creative_failed' && row.groupId ? <OpenPromptGroup groupId={row.groupId} /> : null}
      {row.kind === 'creative_failed' ? <FillAgain countryKey={countryKey} /> : null}
      {onEditContainer && row.containerId ? (
        <SectionAction icon={PenLine} onClick={() => onEditContainer(row.containerId!)}>
          Open {row.containerName ?? 'the container'}
        </SectionAction>
      ) : null}
    </span>
  );
};

export const ErrorDetail: FC<{ row: DropGenErrorRow; countryKey: string; onEditContainer?: (containerId: string) => void }> = ({ row, countryKey, onEditContainer }) => {
  const clock = useCountryClock();
  const [swap, setSwap] = useState(false);
  const w = kindWords(row.kind);
  const details = [
    row.dropAt ? `Drop ${instantText(row.dropAt, clock)}${row.listName ? ` · ${row.listName}` : ''}${row.attempt !== null ? ` · attempt ${row.attempt}` : ''}` : null,
    row.repeats > 1 ? `Happened ${row.repeats} times, first ${instantText(row.at, clock)}, last ${instantText(row.lastAt, clock)}` : null,
    row.resolvedAt ? `Resolved ${instantText(row.resolvedAt, clock)} — ${row.resolution}` : null,
    row.notifyError ? `Slack: ${row.notifyError}` : null,
  ].filter(Boolean);
  return (
    <div className="flex flex-col gap-3 bg-muted/20 px-5 py-3 duration-200 animate-in fade-in-0 motion-reduce:animate-none" data-testid="error-detail">
      <div className="grid gap-4 md:grid-cols-3">
        <Block title="What happened">
          {row.headline}
          {row.next ? <span className="block text-muted-foreground">Then: {row.next}.</span> : null}
        </Block>
        <Block title="Why" tint="text-warning">
          {row.message}
        </Block>
        <Block title="What to do" tint={row.resolvedAt ? 'text-success' : 'text-primary'}>
          {row.resolvedAt ? <Resolved row={row} /> : <>{w.fix}<Actions row={row} countryKey={countryKey} onSwap={() => setSwap(true)} onEditContainer={onEditContainer} /></>}
        </Block>
      </div>
      {swap && !row.resolvedAt ? <SwapPanel countryKey={countryKey} errorId={row.id} onClose={() => setSwap(false)} /> : null}
      {details.length > 0 ? <p className="text-[11px] text-muted-foreground">{details.join(' · ')}</p> : null}
    </div>
  );
};
